import React, { useEffect } from 'react';

const TermsOfService = ({ navigateTo }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="doc-page">
      <div className="doc-hero">
        <div className="doc-breadcrumb">
          <button onClick={() => navigateTo('home')}>SequPay</button>
          <span>›</span><span>Terms of Service</span>
        </div>
        <h1>Terms of Service</h1>
        <p>Effective Date: 1 May 2025 &nbsp;·&nbsp; SequSpace Technologies Pvt. Ltd.</p>
      </div>
      <div className="doc-body">
        <div className="doc-alert">
          These Terms govern your use of the SequPay mobile application and website. By accessing or using the Services in any manner, you acknowledge you have read, understood, and agree to be bound by these Terms. If you do not agree, cease using the Services immediately.
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">1</span>Introduction &amp; Acceptance of Terms</h2>
          <p>These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and <strong>SequSpace Technologies Pvt. Ltd.</strong> ("Company", "we", "us", or "our"), governing your access to the SequPay mobile application, website, and all related services (collectively, "Services").</p>
          <p>The Company reserves the right to update, modify, or revise these Terms at any time. We will provide at least 14 days' notice of material changes via in-app notification or email.</p>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">2</span>User Eligibility &amp; Warranties</h2>
          <p>To access and use our Services, you represent and warrant that:</p>
          <ul>
            <li>You are at least <strong>18 years of age</strong> and possess the legal capacity to enter into a binding contract</li>
            <li>You are not barred from receiving financial services under the laws of any applicable jurisdiction</li>
            <li>All information you provide during onboarding and identity verification is accurate, complete, and current</li>
            <li>You will update your information promptly if it changes</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">3</span>Account Registration &amp; Security</h2>
          <p>Full utilisation of the Services requires creation of a verified SequPay account. As the account holder, you agree to:</p>
          <ul>
            <li>Maintain strict confidentiality of your login credentials, biometric data, PIN, and multi-factor authentication codes</li>
            <li>Accept full responsibility for all activities conducted under your account</li>
            <li>Immediately notify our support team at <a href="mailto:support@sequspace.com">support@sequspace.com</a> if you suspect any unauthorised access</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">4</span>Scope &amp; Nature of Services (Early Access)</h2>
          <p>SequPay is currently an <strong>early-access platform</strong>. The application provides UI demonstrations and previews of payment, UPI, wallet, bill payments, lending, investment, and insurance features. <strong>No live financial transactions are currently processed.</strong></p>
          <p>Live financial features will be activated progressively as:</p>
          <ul>
            <li>Banking partner integrations are completed and tested</li>
            <li>Required regulatory licences are obtained from relevant authorities (RBI, NPCI, SEBI, IRDAI as applicable)</li>
            <li>KYC/AML compliance frameworks are fully implemented and audited</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">5</span>Prohibited Conduct &amp; Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Execute or attempt transactions related to fraudulent enterprises, prohibited goods, or illicit gambling</li>
            <li>Impersonate any individual, corporate entity, or government official</li>
            <li>Deploy bots, scrapers, malware, or tools designed to reverse-engineer the platform</li>
            <li>Attempt to gain unauthorised access to other users' accounts or our servers</li>
            <li>Use the Services to harass, abuse, or defraud other users</li>
            <li>Circumvent any security or fraud-prevention systems</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">6</span>Transaction Execution (When Live)</h2>
          <p>When live financial features are activated:</p>
          <ul>
            <li>By initiating a transaction, you provide irrevocable authorisation to SequPay and its banking partners</li>
            <li>Completed transactions are final unless specifically reversible under governing law</li>
            <li>SequPay disclaims liability for user-input errors (incorrect recipient UPI IDs, erroneous amounts, etc.)</li>
            <li>Transaction disputes must be initiated within the timeframe specified by the applicable payment network</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">7</span>Fees &amp; Charges</h2>
          <p>Basic tier Services are currently provided free of charge. SequPay reserves the right to introduce fees when live financial features are activated.</p>
          <ul>
            <li>All applicable fees will be transparently disclosed within the app prior to final transaction confirmation</li>
            <li>You bear sole responsibility for reporting and remitting any applicable taxes</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">8</span>Third-Party Services &amp; Infrastructure</h2>
          <p>The Company disclaims liability for:</p>
          <ul>
            <li>Systemic outages, latency, or failures instigated by third-party entities</li>
            <li>Delayed transaction settlements resulting from banking network maintenance or Force Majeure events</li>
            <li>Any losses stemming from errors outside the direct technological control of SequPay</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">9</span>Account Suspension &amp; Termination</h2>
          <p>SequPay retains the right to restrict, suspend, or permanently disable your account if:</p>
          <ul>
            <li>We identify material breaches of these Terms</li>
            <li>Our fraud-detection systems flag high-risk or suspicious activity</li>
            <li>We are compelled to do so by a court order or regulatory authority</li>
            <li>Your account has been inactive for an extended period</li>
          </ul>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">10</span>Limitation of Liability</h2>
          <p>To the maximum extent permitted by applicable law, SequSpace Technologies Pvt. Ltd. shall not be liable for:</p>
          <ul>
            <li>Any indirect, incidental, punitive, special, or consequential damages</li>
            <li>Loss of funds attributable to user negligence, phishing, or social engineering</li>
            <li>Service interruptions resulting from Force Majeure events or third-party dependencies</li>
          </ul>
          <p>Our aggregate liability shall not exceed the fees you paid to us in the three months preceding the claim.</p>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">11</span>Intellectual Property</h2>
          <p>All source code, trademarks, brand identities, visual designs, and platform architecture remain the exclusive intellectual property of SequSpace Technologies Pvt. Ltd. You are granted a limited, non-exclusive, revocable licence to use the platform for its intended personal use.</p>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">12</span>Data Privacy</h2>
          <p>Your use of the Services is also governed by our <button onClick={() => navigateTo('privacy')} style={{cursor:'pointer',color:'var(--trustBlue)',background:'none',border:'none',padding:0,fontSize:'inherit',fontFamily:'inherit'}}>Privacy Policy</button>, which is incorporated into these Terms by reference.</p>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">13</span>Governing Law &amp; Dispute Resolution</h2>
          <p>These Terms are governed by the laws of the Republic of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka, India.</p>
          <p>We encourage users to first contact us at <a href="mailto:support@sequspace.com">support@sequspace.com</a> to resolve disputes informally.</p>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">14</span>Amendments</h2>
          <p>The Company reserves the right to amend these Terms with reasonable notice. Amended terms will be published in the app and on our website with an updated Effective Date.</p>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">15</span>Contact</h2>
          <div className="doc-contact">
            <h3>SequSpace Technologies Pvt. Ltd.</h3>
            <p><strong style={{color:'#fff'}}>Support:</strong> <a href="mailto:support@sequspace.com">support@sequspace.com</a><br/>
            <strong style={{color:'#fff'}}>Legal / Privacy:</strong> <a href="mailto:privacy@sequspace.com">privacy@sequspace.com</a><br/>
            <strong style={{color:'#fff'}}>Website:</strong> <a href="https://sequspace.com" target="_blank" rel="noopener noreferrer">https://sequspace.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
